(() => {
  "use strict";

  window.MinacoHeaderFeatures = window.MinacoHeaderFeatures || {};

  window.MinacoHeaderFeatures.search = (MinacoHeader, SELECTORS) => {
    class MinacoHeaderSearchFeature {
    bindSearch() {
      if (!this.searchPanel) {
        return;
      }

      this.searchTriggers.forEach((trigger) => {
        this.on(trigger, "click", (event) => {
          event.preventDefault();
          this.openSearch(trigger);
        });
      });

      this.searchCloseButtons.forEach((button) => {
        this.on(button, "click", () => {
          this.closeSearch({
            restoreFocus: true,
          });
        });
      });

      this.on(this.searchInput, "input", () => this.handleSearchInput());

      this.on(this.searchInput, "keydown", (event) => {
        if (event.key !== "ArrowDown") {
          return;
        }

        const firstResult = this.searchResults?.querySelector("a[href]");

        if (!firstResult) {
          return;
        }

        event.preventDefault();
        firstResult.focus();
      });

      this.on(this.searchResults, "keydown", (event) => {
        if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
          return;
        }

        const links = [...this.searchResults.querySelectorAll("a[href]")];

        const index = links.indexOf(document.activeElement);

        if (index < 0) {
          return;
        }

        event.preventDefault();

        const nextIndex =
          event.key === "ArrowDown"
            ? Math.min(index + 1, links.length - 1)
            : index - 1;

        if (nextIndex < 0) {
          this.searchInput?.focus();
        } else {
          links[nextIndex]?.focus();
        }
      });

      this.on(this.searchForm, "submit", (event) => {
        if (!this.searchInput?.value.trim()) {
          event.preventDefault();
        }
      });
    }

    openSearch(trigger = null) {
      if (!this.searchPanel) {
        return;
      }

      this.searchReturnFocus = trigger || document.activeElement;

      this.closeMegaMenus({
        reconcile: false,
      });

      this.closeCountrySelector({
        reconcile: false,
      });

      this.closeMobileDrawer({
        reconcile: false,
      });

      this.searchOpen = true;

      this.searchPanel.classList.add("is-open");

      this.searchPanel.setAttribute("aria-hidden", "false");

      this.searchTriggers.forEach((item) => {
        item.setAttribute("aria-expanded", "true");
      });

      this.reconcileState();
      this.syncDocumentScrollLock();

      const delay = this.reducedMotionMedia.matches ? 0 : 90;

      this.setTimer("searchFocus", () => this.searchInput?.focus(), delay);
    }

    closeSearch({ restoreFocus = false, reconcile = true } = {}) {
      if (!this.searchPanel) {
        return;
      }

      const wasOpen = this.searchOpen;

      this.searchOpen = false;

      this.searchPanel.classList.remove("is-open");

      this.searchPanel.setAttribute("aria-hidden", "true");

      this.searchTriggers.forEach((item) => {
        item.setAttribute("aria-expanded", "false");
      });

      this.clearTimer("searchDebounce");
      this.clearTimer("searchFocus");
      this.abortSearchRequest();
      this.syncDocumentScrollLock();

      if (reconcile) {
        this.reconcileState();
      }

      if (restoreFocus && wasOpen && this.isConnected(this.searchReturnFocus)) {
        this.searchReturnFocus.focus();
      }
    }

    handleSearchInput() {
      const query = this.searchInput?.value.trim() || "";

      this.clearTimer("searchDebounce");
      this.abortSearchRequest();

      if (query.length < this.searchMinimumCharacters) {
        this.searchResults?.replaceChildren();
        this.searchResults?.setAttribute("aria-busy", "false");

        return;
      }

      const cacheKey = query.toLocaleLowerCase();

      if (this.searchCache.has(cacheKey)) {
        this.renderSearchProducts(this.searchCache.get(cacheKey), query);

        return;
      }

      this.renderSearchMessage(this.label("searchLoading", "Searching…"), true);

      this.setTimer(
        "searchDebounce",
        () => this.fetchSearchResults(query),
        180,
      );
    }

    async fetchSearchResults(query) {
      if (!this.searchResults) {
        return;
      }

      this.abortSearchRequest();

      this.searchRequest = new AbortController();

      const request = this.searchRequest;

      const root = window.Shopify?.routes?.root || "/";

      const endpoint = `${root.replace(/\/?$/, "/")}search/suggest.json`;

      const parameters = new URLSearchParams({
        q: query,
      });

      parameters.set("resources[type]", "product");

      parameters.set("resources[limit]", String(this.searchLimit));

      parameters.set("resources[options][unavailable_products]", "hide");

      parameters.set(
        "resources[options][fields]",
        "title,product_type,variants.title,vendor",
      );

      try {
        const response = await fetch(`${endpoint}?${parameters}`, {
          headers: {
            Accept: "application/json",
          },

          signal: request.signal,
        });

        if (!response.ok) {
          throw new Error(`Predictive search returned ${response.status}`);
        }

        const data = await response.json();

        if (request !== this.searchRequest) {
          return;
        }

        const currentQuery = this.searchInput?.value.trim() || "";

        if (currentQuery !== query) {
          return;
        }

        const products = data?.resources?.results?.products || [];

        this.searchCache.set(query.toLocaleLowerCase(), products);

        this.renderSearchProducts(products, query);
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }

        console.warn("[Mina&Co Header] Predictive search failed.", error);

        this.renderSearchMessage(
          this.label(
            "searchError",
            "Unable to load results. Please try again.",
          ),
        );
      } finally {
        if (request === this.searchRequest) {
          this.searchRequest = null;
        }
      }
    }

    renderSearchMessage(message, busy = false) {
      if (!this.searchResults) {
        return;
      }

      const wrapper = document.createElement("div");

      wrapper.className = "minaco-search__initial";

      wrapper.setAttribute("role", "status");

      wrapper.textContent = message;

      this.searchResults.replaceChildren(wrapper);

      this.searchResults.setAttribute("aria-busy", busy ? "true" : "false");
    }

    renderSearchProducts(products, query) {
      if (!this.searchResults) {
        return;
      }

      this.searchResults.setAttribute("aria-busy", "false");

      if (!products.length) {
        this.renderSearchMessage(
          this.label(
            "searchEmpty",
            `No products found for “${query}”.`,
          ).replace("{{ query }}", query),
        );

        return;
      }

      const fragment = document.createDocumentFragment();

      const grid = document.createElement("div");

      grid.className = "minaco-search__products";

      grid.setAttribute("role", "list");

      products.forEach((product) => {
        grid.appendChild(this.createSearchProduct(product));
      });

      fragment.appendChild(grid);

      const viewAll = document.createElement("a");

      viewAll.className = "minaco-search__view-all minaco-button";

      viewAll.href = this.buildSearchUrl(query);

      viewAll.textContent = this.label("searchViewAll", "View all results");

      fragment.appendChild(viewAll);

      this.searchResults.replaceChildren(fragment);
    }

    createSearchProduct(product) {
      const link = document.createElement("a");

      link.className = "minaco-search__product";

      link.href = product.url || this.buildSearchUrl(product.title || "");

      link.setAttribute("role", "listitem");

      const media = document.createElement("div");

      media.className = "minaco-search__product-media";

      const featuredImage = product.featured_image || product.image;

      const imageUrl =
        typeof featuredImage === "string" ? featuredImage : featuredImage?.url;

      if (imageUrl) {
        const image = document.createElement("img");

        image.className = "minaco-search__product-image";

        image.src = imageUrl;

        image.alt = featuredImage?.alt || product.title || "";

        image.loading = "lazy";
        image.decoding = "async";

        media.appendChild(image);
      }

      const content = document.createElement("div");

      content.className = "minaco-search__product-content";

      const title = document.createElement("span");

      title.className = `minaco-search__product-title minaco-font--${this.searchProductTitleFont}`;

      title.textContent = product.title || "";

      content.appendChild(title);

      const formattedPrice = this.formatProductPrice(product.price);

      if (formattedPrice) {
        const price = document.createElement("span");

        price.className = `minaco-search__product-price minaco-font--${this.searchProductPriceFont}`;

        price.textContent = formattedPrice;

        content.appendChild(price);
      }

      link.append(media, content);

      return link;
    }

    formatProductPrice(value) {
      if (value === null || value === undefined || value === "") {
        return "";
      }

      const raw = String(value).trim();

      if (/[^\d.,\s-]/.test(raw)) {
        return raw;
      }

      const normalized = raw.replace(/\s/g, "").replace(",", ".");

      const number = Number(normalized);

      if (!Number.isFinite(number)) {
        return raw;
      }

      const currency =
        window.Shopify?.currency?.active ||
        this.header.dataset.currency ||
        "USD";

      try {
        return new Intl.NumberFormat(document.documentElement.lang || "en", {
          style: "currency",
          currency,
        }).format(number);
      } catch (_error) {
        return `${number.toFixed(2)} ${currency}`;
      }
    }

    buildSearchUrl(query) {
      const root = window.Shopify?.routes?.root || "/";

      const url = new URL(
        `${root.replace(/\/?$/, "/")}search`,
        location.origin,
      );

      url.searchParams.set("q", query);
      url.searchParams.set("type", "product");

      return `${url.pathname}${url.search}`;
    }

    abortSearchRequest() {
      this.searchRequest?.abort();
      this.searchRequest = null;

      this.searchResults?.setAttribute("aria-busy", "false");
    }

    }

    Object.getOwnPropertyNames(MinacoHeaderSearchFeature.prototype).forEach((methodName) => {
      if (methodName === "constructor") {
        return;
      }

      Object.defineProperty(
        MinacoHeader.prototype,
        methodName,
        Object.getOwnPropertyDescriptor(MinacoHeaderSearchFeature.prototype, methodName),
      );
    });
  };
})();
